"use client";

/**
 * File is based on Builder.io's Image component, with modifications to fit our use case and tech stack.
 * https://github.com/BuilderIO/builder/blob/main/packages/sdks/src/blocks/image/component-info.ts
 */
import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { DEFAULT_IMAGE, IMAGE_FILE_TYPES } from "@/libs/builder/consts";

const BUILDER_IMAGE_DEFAULT_ASPECT_RATIO = 0.7041;

const BUILDER_IMAGE_ICON =
  "https://firebasestorage.googleapis.com/v0/b/builder-3b0a2.appspot.com/o/images%2Fbaseline-insert_photo-24px.svg?alt=media&token=4e5d0ef4-f5e8-4e57-b3a9-38d63a9b9dc4";

function onBuilderImageChange(options: {
  delete: (key: string) => void;
  get: (key: string) => string | number | undefined;
  set: (key: string, value: string | number | boolean) => void;
}) {
  function loadImage(url: string, timeout = 60000): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const cleanup = () => {
        clearTimeout(timer);
        img.onload = null;
        img.onerror = null;
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error(`Image load timed out: ${url}`));
      }, timeout);

      img.onload = () => {
        cleanup();
        resolve(img);
      };

      img.onerror = (error) => {
        cleanup();
        reject(error instanceof Error ? error : new Error(`Image load failed: ${url}`));
      };

      img.src = url;
    });
  }

  function round(value: number) {
    return Math.round(value * 1000) / 1000;
  }

  const value = options.get("image");
  const aspectRatio = options.get("aspectRatio");

  options.delete("srcset");
  options.delete("noWebp");

  if (!value || typeof value !== "string") {
    return;
  }

  void fetch(value)
    .then((response) => response.blob())
    .then((blob) => {
      if (blob.type.includes("svg")) {
        options.set("noWebp", true);
      }
    })
    .catch(() => undefined);

  if (!aspectRatio || aspectRatio === BUILDER_IMAGE_DEFAULT_ASPECT_RATIO) {
    return loadImage(value).then((img) => {
      const possiblyUpdatedAspectRatio = options.get("aspectRatio");

      if (
        options.get("image") === value &&
        (!possiblyUpdatedAspectRatio || possiblyUpdatedAspectRatio === BUILDER_IMAGE_DEFAULT_ASPECT_RATIO)
      ) {
        if (img.width && img.height) {
          options.set("aspectRatio", round(img.height / img.width));
          options.set("height", img.height);
          options.set("width", img.width);
        }
      }
    });
  }
}

export const builderImageInputs = [
  {
    name: "image",
    type: "file",
    bubble: true,
    allowedFileTypes: IMAGE_FILE_TYPES,
    required: true,
    defaultValue: DEFAULT_IMAGE,
    onChange: onBuilderImageChange,
  },
  {
    name: "backgroundSize",
    type: "text",
    defaultValue: "cover",
    enum: [
      {
        label: "contain",
        value: "contain",
        helperText: "The image should never get cropped",
      },
      {
        label: "cover",
        value: "cover",
        helperText: "The image should fill it's box, cropping when needed",
      },
    ],
  },
  {
    name: "backgroundPosition",
    type: "text",
    defaultValue: "center",
    enum: ["center", "top", "left", "right", "bottom", "top left", "top right", "bottom left", "bottom right"],
  },
  {
    name: "altText",
    type: "string",
    helperText: "Text to display when the user has images off",
  },
  {
    name: "title",
    type: "string",
    helperText: "Text to display when hovering over the asset",
  },
  {
    name: "height",
    type: "number",
    hideFromUI: true,
  },
  {
    name: "width",
    type: "number",
    hideFromUI: true,
  },
  {
    name: "sizes",
    type: "string",
    hideFromUI: true,
  },
  {
    name: "srcset",
    type: "string",
    hideFromUI: true,
  },
  {
    name: "lazy",
    type: "boolean",
    defaultValue: true,
    hideFromUI: true,
  },
  {
    name: "fitContent",
    type: "boolean",
    helperText: "When child blocks are provided, fit to them instead of using the image's aspect ratio",
    defaultValue: true,
  },
  {
    name: "highPriority",
    type: "boolean",
    advanced: true,
    helperText:
      "Mark this image as high priority compared to other images on the page. This prevents lazy loading of the image and tells the browser to load this image before others on the page.",
  },
  {
    name: "aspectRatio",
    type: "number",
    helperText:
      "This is the ratio of height/width, e.g. set to 1.5 for a 300px wide and 200px tall photo. Set to 0 to not force the image to maintain it's aspect ratio",
    advanced: true,
    defaultValue: BUILDER_IMAGE_DEFAULT_ASPECT_RATIO,
  },
] as const satisfies Inputs;

export const BuilderImageComponent: RegisteredComponent = {
  name: "Image",
  static: true,
  image: BUILDER_IMAGE_ICON,
  defaultStyles: {
    position: "relative",
    minHeight: "20px",
    minWidth: "20px",
    overflow: "hidden",
  },
  canHaveChildren: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  component: dynamic(async () => (await import("./BuilderImage")).BuilderImage),
  inputs: builderImageInputs,
};
